import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Navigation } from '../components/Navigation';
import { FloatingWhatsApp } from '../components/FloatingWhatsApp';
import { mockData } from '../mock';

const phrases = ['A Website', 'A ChatBot', 'A Mobile App'];

export const Home = () => {
  const observerRef = useRef(null);
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = document.querySelectorAll('.observe-animation');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    const current = phrases[wordIndex];
    const isFullWord = text === current && !isDeleting;
    const isEmptyWord = text === '' && isDeleting;

    let timeout;

    if (isFullWord) {
      timeout = setTimeout(() => setIsDeleting(true), 1200);
      return () => clearTimeout(timeout);
    }

    if (isEmptyWord) {
      timeout = setTimeout(() => {
        setIsDeleting(false);
        setWordIndex((prev) => (prev + 1) % phrases.length);
      }, 500);
      return () => clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      setText((prev) => {
        const nextLength = prev.length + (isDeleting ? -1 : 1);
        return current.slice(0, nextLength);
      });
    }, isDeleting ? 90 : 160);

    return () => clearTimeout(timeout);
  }, [text, isDeleting, wordIndex]);

  const aiPerfectFor = ['Coaches', 'Salons', 'Real Estate Agents', 'E-commerce Stores', 'Local Businesses', 'Startups'];
  const aiYouGet = [
    'WhatsApp / Instagram / Website AI Chatbot',
    'Auto Lead Capture System',
    'Automated Follow-Ups',
    'CRM Setup (Google Sheet/HubSpot)',
    'Booking System Integration',
    'Notifications & Email Automations',
    'Complete Setup + 7-Day Support'
  ];
  const aiWhyWorks = [
    '24/7 lead capture',
    'Higher conversion rate',
    'Fully automated workflow',
    'Saves 10-15 hours weekly',
    'Works for both Indian & foreign businesses'
  ];
  const webServices = [
    'React & Next.js Websites',
    'Modern Business Sites',
    'E-commerce Stores',
    'Portfolio & Personal Brand Websites',
    'Landing Pages + Funnels',
    'React Native Mobile Apps',
    'Admin Dashboards',
    'API Integrations'
  ];
  const webWhy = [
    'Clean, modern UI/UX',
    'Mobile-friendly',
    'SEO ready',
    'Fast delivery (5-10 days)',
    'Affordable for Indian clients',
    'Premium quality for international clients',
    'End-to-end setup (hosting, domain, SSL, email)'
  ];
  const pricing = [
    {
      title: 'AI Automations',
      india: 'INR 9,999 - 24,999',
      global: '$149 - $399'
    },
    {
      title: 'Websites',
      india: 'INR 14,999 - 49,999',
      global: '$249 - $899'
    },
    {
      title: 'Mobile Apps (React Native)',
      india: 'INR 39,999 - 1,20,000',
      global: '$599 - $2,499'
    },
    {
      title: 'Monthly Maintenance',
      india: 'INR 999 - 4,999 / month',
      global: '$15 - $49 / month'
    }
  ];
  const testimonials = [
    {
      id: 1,
      text: 'Fast, professional & exceeded expectations.'
    },
    {
      id: 2,
      text: 'Automation saved us hours every week.'
    },
    {
      id: 3,
      text: 'Website quality is amazing for the price.'
    },
    {
      id: 4,
      text: 'Very communicative, finished everything on time.'
    },
    {
      id: 5,
      text: 'Leads started coming in within days of launch.'
    },
    {
      id: 6,
      text: 'The AI follow-ups feel personal and close deals faster.'
    }
  ];
  const testimonialLoop = [...testimonials, ...testimonials];

  return (
    <div className="relative min-h-screen bg-neutral-950 text-white overflow-hidden">
      <Navigation />
      <FloatingWhatsApp />

      <div className="pointer-events-none absolute inset-0">
        <div className="gradient-orb orb-1" />
        <div className="gradient-orb orb-2" />
        <div className="gradient-orb orb-3" />
        <div className="grain-overlay" />
      </div>

      <main className="relative max-w-6xl lg:max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <section
          id="hero"
          className="relative min-h-screen pt-28 lg:pt-32 pb-20 flex items-center justify-center text-center observe-animation"
        >
          <div className="absolute inset-0 pointer-events-none">
            <div className="hero-veil" />
          </div>
          <div className="max-w-4xl space-y-7 relative">
            <p className="text-sm uppercase tracking-[0.35em] text-white/60">Global delivery</p>
            <div className="news-ticker text-base sm:text-lg font-semibold text-white/80">
              <span className="news-ticker__label">Update</span>
              <div className="news-ticker__viewport">
                <div className="news-ticker__track">
                  <span className="news-ticker__item">Introducing Tara — our AI matchmaker for WhatsApp.</span>
                  <span className="news-ticker__item">Introducing Tara — our AI matchmaker for WhatsApp.</span>
                </div>
              </div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight">
              Websites, Apps & AI Automations That Grow Your Business - Globally
            </h1>
            <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto">
              I help businesses in India, UK, and the US build modern websites, mobile apps, and AI automations that increase leads,
              save time, and accelerate growth.
            </p>
            <div className="text-lg sm:text-xl font-medium text-white/80">
              <span className="text-white/60">Build&nbsp;</span>
              <span className="typed-wrap">
                <span className="typed-text">{text}</span>
                <span className="caret" />
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                className="bg-white text-neutral-900 hover:bg-white/90 px-7 py-6"
                onClick={() => window.open(mockData.hero.cta.bookCall.link, '_blank', 'noopener,noreferrer')}
              >
                Book Free Consultation
              </Button>
              <button
                className="text-sm font-medium text-white/80 hover:text-white underline decoration-white/40 underline-offset-4"
                onClick={() => window.open(mockData.contact.whatsappLink, '_blank', 'noopener,noreferrer')}
              >
                WhatsApp Chat
              </button>
            </div>
            <p className="text-sm text-white/60">
              Serving clients in India, UK & USA · Fast Delivery · Affordable Pricing
            </p>
          </div>
        </section>

        <section id="services" className="py-20 border-t border-white/10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="space-y-4 observe-animation">
              <Badge className="bg-white/10 text-white border-white/10 px-4 py-2 rounded-full w-fit">
                AI Automation Package
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-semibold">AI Automation Setup for Businesses (Starting $149 / INR 9,999)</h2>
              <p className="text-lg text-white/60 max-w-2xl">
                Automate lead generation, bookings, follow-ups, and customer engagement using AI.
              </p>
            </div>
            <button
              className="text-sm font-medium text-white/80 hover:text-white underline decoration-white/40 underline-offset-4"
              onClick={() => window.open(mockData.hero.cta.bookCall.link, '_blank', 'noopener,noreferrer')}
            >
              Book Free Automation Audit -&gt;
            </button>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            <Card className="bg-white/5 border border-white/10 rounded-2xl observe-animation">
              <CardContent className="p-6 space-y-4">
                <p className="text-lg font-semibold text-white">Perfect For</p>
                <div className="space-y-2 text-sm text-white/70">
                  {aiPerfectFor.map((item) => (
                    <p key={item}>- {item}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10 rounded-2xl observe-animation">
              <CardContent className="p-6 space-y-4">
                <p className="text-lg font-semibold text-white">You Get</p>
                <div className="space-y-2 text-sm text-white/70">
                  {aiYouGet.map((item) => (
                    <p key={item}>- {item}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10 rounded-2xl observe-animation">
              <CardContent className="p-6 space-y-4">
                <p className="text-lg font-semibold text-white">Why This Works</p>
                <div className="space-y-2 text-sm text-white/70">
                  {aiWhyWorks.map((item) => (
                    <p key={item}>- {item}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section id="work" className="py-20 border-t border-white/10">
          <div className="space-y-4 observe-animation">
            <Badge className="bg-white/10 text-white border-white/10 px-4 py-2 rounded-full w-fit">
              Website & App Development
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-semibold">Website & App Development (Starting $249 / INR 14,999)</h2>
            <p className="text-lg text-white/60 max-w-3xl">
              Modern, fast, and conversion-focused digital products built for Indian and global businesses.
            </p>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <Card className="bg-white/5 border border-white/10 rounded-2xl observe-animation">
              <CardContent className="p-6 space-y-4">
                <p className="text-lg font-semibold text-white">Services Include</p>
                <div className="grid gap-2 text-sm text-white/70 sm:grid-cols-2">
                  {webServices.map((item) => (
                    <p key={item}>- {item}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10 rounded-2xl observe-animation">
              <CardContent className="p-6 space-y-4">
                <p className="text-lg font-semibold text-white">Why Work With Me</p>
                <div className="space-y-2 text-sm text-white/70">
                  {webWhy.map((item) => (
                    <p key={item}>- {item}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              className="w-fit bg-white text-neutral-900 hover:bg-white/90"
              onClick={() => navigate('/portfolio')}
            >
              View Portfolio -&gt;
            </Button>
            <button
              className="text-sm font-medium text-white/80 hover:text-white underline decoration-white/40 underline-offset-4"
              onClick={() => window.open(mockData.hero.cta.bookCall.link, '_blank', 'noopener,noreferrer')}
            >
              Start Your Project -&gt;
            </button>
          </div>
        </section>

        <section className="py-16 border-y border-white/10">
          <div className="space-y-4 mb-10 observe-animation">
            <Badge className="bg-white/10 text-white border-white/10 px-4 py-2 rounded-full w-fit">
              Pricing
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-semibold">Simple & Transparent</h2>
            <p className="text-white/60 max-w-2xl">
              Typical pricing range for both Indian and international clients.
            </p>
          </div>
          <div className="grid gap-6 lg:grid-cols-4">
            {pricing.map((item) => (
              <Card key={item.title} className="bg-white/5 border border-white/10 rounded-2xl observe-animation">
                <CardContent className="p-6 space-y-4">
                  <p className="text-lg font-semibold text-white">{item.title}</p>
                  <div className="space-y-2 text-sm text-white/70">
                    <p>India: {item.india}</p>
                    <p>UK/US: {item.global}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="py-20">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] items-start observe-animation">
            <div className="space-y-4">
              <Badge className="bg-white/10 text-white border-white/10 px-4 py-2 rounded-full w-fit">About</Badge>
              <h2 className="text-3xl sm:text-4xl font-semibold">Hi, I'm Pranshik</h2>
              <p className="text-lg text-white/70">
                A full-stack developer specializing in React Native, Next.js, and AI automations. I've built 12+ production apps, worked
                with startups, and delivered projects for clients across India, UK, and the US.
              </p>
            </div>
            <Card className="bg-white/5 border border-white/10 rounded-2xl">
              <CardContent className="p-6 space-y-3 text-sm text-white/70">
                <p>Focused on:</p>
                <p>- Clean design</p>
                <p>- Fast delivery</p>
                <p>- Scalable architecture</p>
                <p>- Business-first mindset</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section id="testimonials" className="py-20 border-b border-white/10">
          <div className="space-y-4 mb-10 observe-animation">
            <Badge className="bg-white/10 text-white border-white/10 px-4 py-2 rounded-full w-fit">Testimonials</Badge>
            <h2 className="text-3xl sm:text-4xl font-semibold">What Clients Say</h2>
            <p className="text-white/60 max-w-2xl">Feedback from founders and teams across India, UK, and the US.</p>
          </div>
          <div className="relative overflow-hidden">
            <div className="pointer-events-none absolute left-0 top-0 h-full w-24 bg-gradient-to-r from-neutral-950 via-neutral-950/90 to-transparent z-10" />
            <div className="pointer-events-none absolute right-0 top-0 h-full w-24 bg-gradient-to-l from-neutral-950 via-neutral-950/90 to-transparent z-10" />
            <div className="flex gap-6 testimonial-track">
              {testimonialLoop.map((testimonial, idx) => (
                <Card
                  key={`${testimonial.id}-${idx}`}
                  className="relative bg-white/5 border border-white/10 rounded-3xl hover-card overflow-hidden min-w-[260px] sm:min-w-[300px] max-w-[320px]"
                >
                  <div className="absolute -top-16 right-4 h-24 w-24 bg-white/10 blur-2xl" />
                  <CardContent className="p-6 space-y-6">
                    <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-white/50">
                      <span>Client Note</span>
                      <div className="flex items-center gap-1 text-neutral-300">
                        {Array.from({ length: 5 }).map((_, starIdx) => (
                          <Star key={starIdx} size={14} className="fill-neutral-300" />
                        ))}
                      </div>
                    </div>
                    <p className="text-base text-white/80 leading-relaxed italic">"{testimonial.text}"</p>
                    <div className="flex items-center gap-2 text-sm text-white/60">
                      <span className="h-1 w-8 rounded-full bg-white/20" />
                      Verified client
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="brands" className="py-20 border-b border-white/10">
          <div className="space-y-4 mb-10 observe-animation">
            <Badge className="bg-white/10 text-white border-white/10 px-4 py-2 rounded-full w-fit">Projects & Brands</Badge>
            <h2 className="text-3xl sm:text-4xl font-semibold">Tara</h2>
            <p className="text-white/60 max-w-2xl">
              Tara is a Pranshik product — an AI matchmaker for WhatsApp that helps businesses capture leads and respond instantly.
            </p>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="bg-white/5 border border-white/10 rounded-2xl observe-animation">
              <CardContent className="p-6 space-y-4">
                <p className="text-lg font-semibold text-white">Tara</p>
                <p className="text-sm text-white/70">
                  Built and operated by Pranshik to automate WhatsApp conversations, qualifying leads and guiding customers to book.
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-white/60 uppercase tracking-[0.2em]">
                  <span className="px-3 py-1 rounded-full bg-white/10">WhatsApp</span>
                  <span className="px-3 py-1 rounded-full bg-white/10">AI</span>
                  <span className="px-3 py-1 rounded-full bg-white/10">Lead Capture</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10 rounded-2xl observe-animation">
              <CardContent className="p-6 space-y-4">
                <p className="text-lg font-semibold text-white">Pranshik Studio</p>
                <p className="text-sm text-white/70">
                  Product engineering and automation services powering Tara and client systems across web, mobile, and AI workflows.
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-white/60 uppercase tracking-[0.2em]">
                  <span className="px-3 py-1 rounded-full bg-white/10">Web</span>
                  <span className="px-3 py-1 rounded-full bg-white/10">Mobile</span>
                  <span className="px-3 py-1 rounded-full bg-white/10">Automations</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section id="contact" className="py-20">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-neutral-700/35 via-neutral-500/30 to-neutral-800/30 p-10 md:p-12 observe-animation">
            <div className="absolute -top-16 right-16 h-40 w-40 bg-white/20 blur-3xl" />
            <div className="absolute -bottom-20 left-10 h-48 w-48 bg-neutral-500/30 blur-3xl" />
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="space-y-4 max-w-2xl">
                <h2 className="text-3xl sm:text-4xl font-semibold">Ready to Build Something Great?</h2>
                <p className="text-lg text-white/70">Let's create a website, app, or automation that grows your business.</p>
                <p className="text-sm text-white/60">Serving clients in India, UK & USA</p>
                <p className="text-sm text-white/60">WhatsApp: +91 98051 17900</p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Button
                  size="lg"
                  className="bg-white text-neutral-900 hover:bg-white/90 px-7 py-6"
                  onClick={() => window.open('mailto:hey@pranshik.com')}
                >
                  hey@pranshik.com
                </Button>
                <button
                  className="text-sm font-medium text-white/80 hover:text-white underline decoration-white/40 underline-offset-4"
                  onClick={() => window.open(mockData.contact.whatsappLink, '_blank', 'noopener,noreferrer')}
                >
                  WhatsApp Chat (Instant)
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 py-10 px-4 sm:px-6 lg:px-10">
        <div className="max-w-6xl lg:max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-white/70">
          <div>
            <p className="text-xl font-semibold text-white signature-font">Pranshik</p>
            <p className="text-sm text-white/60">Product engineer crafting mobile apps & web platforms.</p>
            <p className="text-xs text-white/50 mt-3">© {new Date().getFullYear()} Pranshik. All rights reserved. Creator of Tara.</p>
          </div>
          <div className="flex flex-wrap gap-6 text-sm">
            <a href="mailto:hey@pranshik.com" className="hover:text-white">hey@pranshik.com</a>
            <span className="text-white/60">WhatsApp: +91 98051 17900</span>
            <button className="hover:text-white" onClick={() => document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' })}>
              Home
            </button>
            <button className="hover:text-white" onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}>
              Services
            </button>
            <button className="hover:text-white" onClick={() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' })}>
              Work
            </button>
            <button className="hover:text-white" onClick={() => document.getElementById('brands')?.scrollIntoView({ behavior: 'smooth' })}>
              Brands
            </button>
            <button className="hover:text-white" onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}>
              Contact
            </button>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes floatOne {
          from { transform: translate3d(0, 0, 0) scale(1); }
          to { transform: translate3d(6%, -4%, 0) scale(1.05); }
        }
        @keyframes floatTwo {
          from { transform: translate3d(0, 0, 0) scale(1); }
          to { transform: translate3d(-8%, 6%, 0) scale(1.08); }
        }
        @keyframes floatThree {
          from { transform: translate3d(0, 0, 0) scale(1); }
          to { transform: translate3d(4%, 8%, 0) scale(1.06); }
        }
        @keyframes blink {
          0%, 45% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        .gradient-orb {
          position: absolute;
          width: 60vw;
          height: 60vw;
          filter: blur(140px);
          opacity: 0.4;
          mix-blend-mode: screen;
        }
        .orb-1 {
          top: -18vh;
          left: -12vw;
          background: radial-gradient(circle at 30% 30%, rgba(196, 196, 196, 0.8), transparent 55%);
          animation: floatOne 18s ease-in-out infinite alternate;
        }
        .orb-2 {
          top: 14vh;
          right: -18vw;
          background: radial-gradient(circle at 40% 40%, rgba(170, 170, 170, 0.65), transparent 60%);
          animation: floatTwo 22s ease-in-out infinite alternate;
        }
        .orb-3 {
          bottom: -14vh;
          left: 18vw;
          background: radial-gradient(circle at 50% 50%, rgba(120, 120, 120, 0.5), transparent 60%);
          animation: floatThree 26s ease-in-out infinite alternate;
        }
        .grain-overlay {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 0);
          background-size: 4px 4px;
          opacity: 0.32;
          mix-blend-mode: soft-light;
        }
        .hero-veil {
          position: absolute;
          inset: -40%;
          background: radial-gradient(70% 70% at 50% 45%, rgba(255, 255, 255, 0.08), transparent 60%);
          filter: blur(60px);
          opacity: 0.8;
        }
        .typed-wrap {
          display: inline-flex;
          align-items: baseline;
          min-height: 1em;
        }
        .caret {
          display: inline-block;
          width: 2px;
          height: 1em;
          background: #fff;
          margin-left: 6px;
          align-self: baseline;
          animation: blink 1.3s steps(1) infinite;
        }
        .typed-text {
          transition: color 0.3s ease;
        }
      `}</style>
    </div>
  );
};
